/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import axios from 'axios';
import { addDays, isEqual, isAfter, startOfDay, isSameDay, isValid } from 'date-fns';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import toNumber from 'lodash/toNumber';
import toString from 'lodash/toString';
import { SecurityHelper } from 'sarsaparilla';

import * as types from '../constants';
import { cloneState } from '../utils/stateUtils';
import { getSearchDocId, milesToKms, getTripStartEndDates } from '../utils/tripUtils';
import { printBlob } from '../utils/printUtils';
import { formatTripPlannerDate } from '../utils/dateUtils';

const MAX_GEO_RESULTS = 300;

// converts trip destination/items to contain only saveable attributes that are accepted by backend
const convertTripToSaveableTrip = (trip) => {
    let ret = trip;
    if (trip) {
        ret = JSON.parse(JSON.stringify(trip));
        ret.destinations.forEach((d) => {
            const destination = d;
            for (const propertyName in destination) {
                if (
                    ![
                        'entity_id',
                        'entity_type',
                        'name',
                        'duration',
                        'travel_duration',
                        'distance',
                        'lat',
                        'lng',
                        'items',
                    ].includes(propertyName)
                ) {
                    delete destination[propertyName];
                }
            }
            destination.items.forEach((i) => {
                const item = i;
                for (const propertyName in item) {
                    if (
                        !['entity_id', 'entity_type', 'name', 'lat', 'lng'].includes(
                            propertyName
                        )
                    ) {
                        delete item[propertyName];
                    }
                }
            });
        });
    }
    return ret;
};

// update if the overview modal should be enabled
export function setEnableOverviewModal(enableOverviewModal) {
    return {
        type: types.TRIP_PLANNER_SET_ENABLE_OVERVIEW_MODAL,
        enableOverviewModal,
    };
}

// save current trip
export const saveTrip = (navigate) => (dispatch, getState) => {
    const state = getState();
    let trip = state.tripPlanner.trip;
    const account = SecurityHelper.getAccount();
    let navigateToTrip = false;
    let showOverviewModal = false;

    if (!trip.trip_id) {
        showOverviewModal = true;
        navigateToTrip = true;
    }

    // check trip validity and save only if valid
    if (!trip || (trip.account_id && trip.account_id !== account.account_id)) {
        trip.trip_id = null;
        trip.account_id = null;
        navigateToTrip = true;
    }

    trip = convertTripToSaveableTrip(state.tripPlanner.trip);
    const params = {
        trip,
    };

    let url = `${process.env.API}/trips/save`;
    if (!account || !account.account_id) {
        url = `${url}/anonymous`;
    }
    return axios
        .post(url, params, {
            headers: {
                Authorization: SecurityHelper.getAuthHeader(),
            },
        })
        .then((json) => {
            if (json.data.err) {
                throw new Error(`Error saving trip ${json.data.err}`);
            } else {
                const tripID = json.data.trip_id;
                if (!tripID) {
                    throw new Error('Failed to save trip');
                } else {
                    dispatch({
                        type: types.TRIP_PLANNER_SET_TRIP_ID,
                        trip_id: tripID,
                    });

                    dispatch({
                        type: types.TRIP_PLANNER_SET_TRIP_SAVED,
                    });

                    // update state so the Overview Modal will display in next page.
                    if (navigateToTrip) {
                        // redirect or replace current url with the trip id
                        if (window.location.pathname.startsWith('/trips/new')) {
                            // navigate to the trip
                            navigate(`/trips/${tripID}`);
                        } else {
                            // update browser history in place, without navigation
                            window.history.replaceState({}, '', `/trips/${tripID}`);
                        }
                        if (showOverviewModal) {
                            setTimeout(() => {
                                dispatch(setEnableOverviewModal(true));
                            }, 500);
                        }
                    }
                }
            }
        })
        .catch(() => {
            // handle system errors using a global axios interceptor?
        });
};

// update trip start date
export function setStartDate(dt) {
    return (dispatch) => {
        dispatch({
            type: types.TRIP_PLANNER_SET_START_DATE,
            date: dt,
        });

        // autosave
        setTimeout(() => {
            dispatch(saveTrip());
        }, 100);
    };
}

// update trip end date
export function setEndDate(dt) {
    return (dispatch) => {
        dispatch({
            type: types.TRIP_PLANNER_SET_END_DATE,
            date: dt,
        });

        // autosave
        setTimeout(() => {
            dispatch(saveTrip());
        }, 100);
    };
}

// update trip name
export function setName(name, navigate) {
    return (dispatch) => {
        dispatch({
            type: types.TRIP_PLANNER_SET_NAME,
            name,
        });

        // autosave
        setTimeout(() => {
            dispatch(saveTrip(navigate));
        }, 100);
    };
}

// update itinerary selected/active date
export function setActiveDate(activeDate) {
    return {
        type: types.TRIP_PLANNER_SET_ACTIVE_DATE,
        activeDate,
    };
}

// update active/highlighted itinerary destination index
export function setActiveDestinationIndex(
    activeDestinationIndex,
    setMapIndex = true,
    setListIndex = true,
    setItineraryIndex = true
) {
    const params = {
        type: types.TRIP_PLANNER_SET_ACTIVE_DESTINATION_INDEX,
        activeDestinationIndex,
    };
    if (setMapIndex) {
        params.activeDestinationMapIndex = activeDestinationIndex;
    }
    if (setListIndex) {
        params.activeDestinationListIndex = activeDestinationIndex;
    }
    if (setItineraryIndex) {
        params.activeDestinationItineraryIndex = activeDestinationIndex;
    }
    return params;
}

// update active/highlighted itinerary destination item index
export function setActiveItineraryItemIndex(activeDestinationIndex, activeItemIndex) {
    const params = {
        type: types.TRIP_PLANNER_SET_ACTIVE_ITEM_INDEX,
        activeDestinationIndex,
        activeItemIndex,
    };
    return params;
}

// update trip planner search radius
export function setBufferRadius(radius) {
    return (dispatch) => {
        dispatch({
            type: types.TRIP_PLANNER_SET_RADIUS,
            radius,
        });
        // autosave
        setTimeout(() => {
            dispatch(saveTrip());
        }, 100);
    };
}

// update trip planner view
export function setView(view) {
    return {
        type: types.TRIP_PLANNER_SET_VIEW,
        view,
    };
}

// update if the book modal should be enabled
export function setDataBookModal(bookingData) {
    return {
        type: types.TRIP_PLANNER_SET_DATA_BOOK_MODAL,
        bookingData,
    };
}

// update if the trip share modal should be shown
export function setShowTripShareModal(showTripShareModal) {
    return {
        type: types.TRIP_PLANNER_SET_SHOW_TRIP_SHARE_MODAL,
        showTripShareModal,
    };
}

// update if the trip itinerary add modal should be shown
export function setShowTripItemAddSModal(showTripItemAddModal, tripItemToAdd) {
    return {
        type: types.TRIP_PLANNER_SET_SHOW_TRIP_ITEM_ADD_MODAL,
        showTripItemAddModal,
        tripItemToAdd,
    };
}

// update if the trip edit route modal
export function setShowEditRouteModal(showEditRouteModal) {
    return {
        type: types.TRIP_PLANNER_SET_SHOW_EDIT_ROUTE_MODAL,
        showEditRouteModal,
    };
}

// update trip guest counts
export function setGuestCounts(guestCounts) {
    return (dispatch) => {
        dispatch({
            type: types.TRIP_PLANNER_SET_GUEST_COUNTS,
            guestCounts,
        });

        // autosave
        setTimeout(() => {
            dispatch(saveTrip());
        }, 100);
    };
}

// update search criteria state with a new set of values
export function updateDestinationSearchCriteria(destinationIndex, params) {
    return {
        type: types.TRIP_PLANNER_UPDATE_DESTINATION_SEARCH_CRITERIA,
        destinationIndex,
        params,
    };
}

// Fetch location suggestions, using search service API
export function fetchDestinationSuggestions(prefix) {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/search/suggest`, {
                params: {
                    q: prefix,
                    geocoder: true,
                },
            })
            .then((json) => {
                if (json.data.err) {
                    dispatch({
                        type: types.GET_TRIP_DESTINATION_SUGGESTIONS_ERROR,
                        message: 'Error fetching suggestions',
                    });
                } else {
                    dispatch({
                        type: types.GET_TRIP_DESTINATION_SUGGESTIONS,
                        payload: json.data,
                    });
                }
            })
            .catch(() => {
                // handle system errors using a global axios interceptor?
            });
    };
}

// generate trip pdf
export function showTripPDF(tripId) {
    return () => {
        return axios
            .get(`${process.env.API}/trips/${tripId}/pdf`, {
                headers: {
                    Authorization: SecurityHelper.getAuthHeader(),
                },
                responseType: 'arraybuffer',
            })
            .then((response) => {
                const blob = new Blob([response.data], { type: 'application/pdf' });
                printBlob(blob);
            });
    };
}

// updateTripPlannerCriterias updates multiple parameters of tripPlanner state
export const updateTripPlannerCriterias = (params) => (dispatch) => {
    const st = { ...params };
    st.type = types.TRIP_PLANNER_UPDATE_CRITERIAS;
    return dispatch(st);
};

// build filters from search state
export function buildSearchFilters(baseFilters, uiFilters) {
    const ret = [];
    // append raw base filters
    if (baseFilters) {
        baseFilters.forEach((filter) => {
            filter.values.forEach((value) => {
                const parts = value.value.split(',');
                parts.forEach((part) => {
                    if (part.startsWith('>') || part.startsWith('<')) {
                        // range filters
                        ret[ret.length] = `${filter.attribute}${part}`;
                    } else {
                        // term filters
                        ret[ret.length] = `${filter.attribute}:${part}`;
                    }
                });
            });
        });
    }

    // convert and append uiFilters to filters
    if (uiFilters) {
        for (const propName in uiFilters) {
            if (isObject(uiFilters[propName])) {
                if (!isEmpty(uiFilters[propName])) {
                    for (const key in uiFilters[propName]) {
                        if (uiFilters[propName][key]) {
                            const uiFilter = uiFilters[propName][key];
                            uiFilter.filters.forEach((filter) => {
                                let alreadySet = false;
                                baseFilters.forEach((f) => {
                                    if (filter.attribute === f.attribute) {
                                        alreadySet = true;
                                    }
                                });
                                if (!alreadySet) {
                                    const parts = filter.filterValue.split(',');
                                    parts.forEach((part) => {
                                        if (
                                            part.startsWith('>') ||
                                            part.startsWith('<')
                                        ) {
                                            // range filters
                                            ret[ret.length] =
                                                `${filter.attribute}${part}`;
                                        } else {
                                            // term filters
                                            ret[ret.length] =
                                                `${filter.attribute}:${part}`;
                                        }
                                    });
                                }
                            });
                        }
                    }
                }
            }
        }
    }

    return ret;
}

// helper method to construct search POST uri
function buildSearchServiceRequest(params) {
    // build filters
    const filters = buildSearchFilters(params.filters, params.uiFilters);

    // build aggregations
    const aggs = [];
    if (params.aggregations) {
        params.aggregations.forEach((agg) => {
            aggs[aggs.length] = agg;
        });
    }

    // create request params
    const ret = {
        q: params.what,
        entity_id: params.entity_id,
        entity_type: params.entity_type,
        exact: params.exact,
        lat: toNumber(params.lat),
        lng: toNumber(params.lng),
        radius: toString(params.radius),
        location: params.location,
        lat_sw: toNumber(params.lat_sw),
        lng_sw: toNumber(params.lng_sw),
        lat_ne: toNumber(params.lat_ne),
        lng_ne: toNumber(params.lng_ne),
        start: params.start != null ? toNumber(params.start) : 0,
        size: params.size != null ? toNumber(params.size) : 4,
        polygon: params.routePolygonStr,
        agg: aggs,
        fq: filters,
        sort: params.sort,
        placeid: params.placeid,
        include_unavailable: true,
        include_partially_available: true,
        group_agg: params.group_agg,
        fg: params.fg,
    };

    if (params.start_date && params.end_date) {
        ret.start_date = params.start_date;
        ret.end_date = params.end_date;
    }

    return ret;
}

// fetch inventory search results for map/geo display
export const fetchTripPlannerMapSearchResults =
    (line, polygon) => (dispatch, getState) => {
        const state = getState();

        if (
            !state.tripPlanner.directions ||
            state.tripPlanner.directions.routes.length < 1 ||
            !state.tripPlanner.directions.routes[0].geometry
        ) {
            // exit if no directions?
            return new Promise((resolve) => {
                resolve();
            });
        }

        const params = {
            filters: [],
            uiFilters: state.tripPlanner.uiFilters,
            radius: null,
            lat: null,
            lng: null,
            start: 0,
            size: MAX_GEO_RESULTS,
            fg: state.tripPlanner.fg,
        };

        let polygonStr = null;

        if (state.tripPlanner.trip.radius !== -1) {
            // if radius/buffer is set, then calculate polygon with buffer/radius along the directions route
            polygonStr = '';
            if (polygon) {
                polygon.geometry.coordinates[0].map((coord) => {
                    if (polygonStr.length > 0) {
                        polygonStr += ',';
                    }
                    polygonStr += `${coord[0]} ${coord[1]}`;
                    return null;
                });
            }

            params.routePolygonStr = polygonStr;
            // include map bounding box
            params.lat_sw = state.tripPlanner.latSW;
            params.lng_sw = state.tripPlanner.lngSW;
            params.lat_ne = state.tripPlanner.latNE;
            params.lng_ne = state.tripPlanner.lngNE;
        } else {
            // if radius/buffer is not set, then find results using a bounding box
            params.lat_sw = state.tripPlanner.latSW;
            params.lng_sw = state.tripPlanner.lngSW;
            params.lat_ne = state.tripPlanner.latNE;
            params.lng_ne = state.tripPlanner.lngNE;
        }

        const tripStartEndDates = getTripStartEndDates(state.tripPlanner.trip);
        const tripStartDate = tripStartEndDates[0];
        const tripEndDate = tripStartEndDates[1];
        const today = startOfDay(new Date());

        if (
            tripStartDate &&
            (isEqual(tripStartDate, today) || isAfter(tripStartDate, today))
        ) {
            params.start_date = formatTripPlannerDate(tripStartDate);
        }
        if (tripEndDate && (isEqual(tripEndDate, today) || isAfter(tripEndDate, today))) {
            params.end_date = formatTripPlannerDate(tripEndDate);
        }

        // exclude indiviual tours from results
        params.filters = [
            {
                attribute: '-entity_type',
                values: [{ value: '(tour OR timedentry_tour)' }],
            },
        ];

        const args = buildSearchServiceRequest(params);
        dispatch({ type: types.TRIP_PLANNER_START_SEARCH_RESULTS });
        return axios
            .post(`${process.env.API}/search/geo`, args)
            .then((json) => {
                dispatch({ type: types.TRIP_PLANNER_END_SEARCH_RESULTS });
                if (json.data.err) {
                    dispatch({
                        type: types.GET_TRIP_PLANNER_SEARCH_ERROR,
                        message: 'Error fetching search results',
                    });
                } else {
                    let result = json.data.results;
                    if (!result) {
                        result = [];
                    }
                    dispatch({
                        type: types.GET_TRIP_PLANNER_SEARCH_GEO_RESULTS,
                        geoResults: result,
                        routePolygon: polygon,
                        routePolygonStr: polygonStr,
                        routeLine: line,
                    });
                }
            })
            .catch(() => {
                // handle system errors using a global axios interceptor?
                dispatch({ type: types.TRIP_PLANNER_END_SEARCH_RESULTS });
            });
    };

// fetch inventory search results for map/geo display
export const fetchTripItemsDetails = () => (dispatch, getState) => {
    const trip = getState().tripPlanner.trip;
    if (isEmpty(trip) || isEmpty(trip.destinations)) {
        return new Promise((resolve) => {
            resolve();
        });
    }

    const searchRequests = [];

    const tripStartEndDates = getTripStartEndDates(trip);
    const tripStartDate = tripStartEndDates[0];
    const tripEndDate = tripStartEndDates[1];

    if (tripStartDate && tripEndDate) {
        let startDate = startOfDay(tripStartDate);
        // collect document ids of all trip destinations
        trip.destinations.forEach((destination) => {
            if (destination.duration) {
                const endDate = addDays(startDate, destination.duration);

                const ids = [];
                if (destination.entity_id) {
                    ids[ids.length] = getSearchDocId(destination);
                }
                destination.items.forEach((item) => {
                    if (item.entity_id) {
                        ids[ids.length] = getSearchDocId(item);
                    }
                });

                let startDateString = null;
                if (
                    isValid(startDate) &&
                    (isAfter(startDate, new Date()) || isSameDay(startDate, new Date()))
                ) {
                    startDateString = formatTripPlannerDate(startDate);
                }

                let endDateString = null;
                if (
                    isValid(endDate) &&
                    (isAfter(endDate, new Date()) || isSameDay(endDate, new Date()))
                ) {
                    endDateString = formatTripPlannerDate(endDate);
                }
                if (ids.length > 0) {
                    const params = {
                        start: 0,
                        size: MAX_GEO_RESULTS,
                        start_date: startDateString,
                        end_date: endDateString,
                        filters: [
                            {
                                attribute: 'id',
                                values: [{ value: ids.join(',') }],
                            },
                        ],
                    };
                    const args = buildSearchServiceRequest(params);
                    searchRequests[searchRequests.length] = args;
                }
                startDate = endDate;
            }
        });
    } else {
        const ids = [];
        // collect document ids of all trip destinations
        trip.destinations.forEach((destination) => {
            if (destination.entity_id) {
                ids[ids.length] = getSearchDocId(destination);
            }
            destination.items.forEach((item) => {
                if (item.entity_id) {
                    ids[ids.length] = getSearchDocId(item);
                }
            });
        });
        if (ids.length > 0) {
            const params = {
                start: 0,
                size: MAX_GEO_RESULTS,
                filters: [
                    {
                        attribute: 'id',
                        values: [{ value: ids.join(',') }],
                    },
                ],
            };
            const args = buildSearchServiceRequest(params);
            searchRequests[searchRequests.length] = args;
        }
    }

    // if there are no entity ids to fetch, then exit
    if (searchRequests.length === 0) {
        return new Promise((resolve) => {
            resolve();
        });
    }

    searchRequests.map((args) => {
        return axios
            .post(`${process.env.API}/search`, args)
            .then((json) => {
                if (json.data.err) {
                    dispatch({
                        type: types.GET_TRIP_PLANNER_SEARCH_ERROR,
                        message: 'Error fetching search results',
                    });
                } else {
                    const results = json.data.results;
                    dispatch({
                        type: types.TRIP_PLANNER_LOAD_ITEMS_DETAILS,
                        results,
                    });
                }
            })
            .catch(() => {
                // handle system errors using a global axios interceptor?
            });
    });

    if (SecurityHelper.isLoggedIn()) {
        axios
            .get(`${process.env.API}/camps/reservations/list`, {
                headers: {
                    Authorization: SecurityHelper.getAuthHeader(),
                },
            })
            .then((json) => {
                if (json.data.err) {
                    dispatch({
                        type: types.GET_TRIP_PLANNER_SEARCH_ERROR,
                        message: 'Error fetching search results',
                    });
                } else {
                    const results = json.data.reservations;
                    dispatch({
                        type: types.TRIP_PLANNER_LOAD_CAMPING_RESERVATIONS,
                        reservations: results,
                    });
                }
            })
            .catch(() => {
                // handle system errors using a global axios interceptor?
            });
        // TODO: there is no such an API in ticket service this needs to be re implemented.
        // axios.get(`${process.env.API}/ticket/reservation/list`, {
        //     headers: {
        //         Authorization: SecurityHelper.getAuthHeader()
        //     }
        // })
        //     .then((json) => {
        //         if (json.data.err) {
        //             dispatch({
        //                 type: types.GET_TRIP_PLANNER_SEARCH_ERROR,
        //                 message: 'Error fetching search results'
        //             });
        //         } else {
        //             const results = json.data;
        //             dispatch({
        //                 type: types.TRIP_PLANNER_LOAD_TICKET_RESERVATIONS,
        //                 reservations: results
        //             });
        //         }
        //     })
        //     .catch(() => {
        //         // handle system errors using a global axios interceptor?
        //     });

        axios
            .get(`${process.env.API}/permits/issuances/list`, {
                headers: {
                    Authorization: SecurityHelper.getAuthHeader(),
                },
            })
            .then((json) => {
                if (json.data.err) {
                    dispatch({
                        type: types.GET_TRIP_PLANNER_SEARCH_ERROR,
                        message: 'Error fetching search results',
                    });
                } else {
                    const results = json.data.payload;
                    dispatch({
                        type: types.TRIP_PLANNER_LOAD_PERMIT_ISSUANCES,
                        issuances: results,
                    });
                }
            })
            .catch(() => {
                // handle system errors using a global axios interceptor?
            });
    }
    return null;
};

// fetch inventory search results
export const fetchDrivingDirections = (trip) => (dispatch) => {
    if (isEmpty(trip.destinations)) {
        // exit if no destinations
        return new Promise((resolve) => {
            resolve();
        });
    }

    const saveableTrip = convertTripToSaveableTrip(trip);

    dispatch({ type: types.TRIP_PLANNER_START_SEARCH_RESULTS });
    return axios
        .post(`${process.env.API}/trips/directions`, {
            destinations: saveableTrip.destinations,
        })
        .then((json) => {
            dispatch({ type: types.TRIP_PLANNER_END_SEARCH_RESULTS });
            if (json.data.err) {
                dispatch({
                    type: types.GET_DIRECTIONS_ERROR,
                    message: 'Error fetching directions',
                });
            } else {
                dispatch({
                    type: types.GET_DIRECTIONS,
                    directions: json.data.directions,
                });
            }
        })
        .catch(() => {
            // handle system errors using a global axios interceptor?
            dispatch({ type: types.TRIP_PLANNER_END_SEARCH_RESULTS });
        });
};

// fetch detailed route for trip
export const fetchDetailedRoute = (trip) => (dispatch) => {
    if (isEmpty(trip.destinations)) {
        // exit if no destinations
        return new Promise((resolve) => {
            resolve();
        });
    }

    const saveableTrip = convertTripToSaveableTrip(trip);

    dispatch({ type: types.TRIP_PLANNER_START_SEARCH_RESULTS });
    return axios
        .post(`${process.env.API}/trips/directions`, {
            destinations: saveableTrip.destinations,
            detailed: true,
        })
        .then((json) => {
            dispatch({ type: types.TRIP_PLANNER_END_SEARCH_RESULTS });
            if (json.data.err) {
                dispatch({
                    type: types.GET_DIRECTIONS_ERROR,
                    message: 'Error fetching detailed route',
                });
            } else {
                const line = {
                    type: 'Feature',
                    properties: {},
                    geometry: json.data.directions.routes[0].geometry,
                };
                dispatch({
                    type: types.GET_TRIP_PLANNER_DETAILED_ROUTE,
                    routeLine: line,
                });
            }
        })
        .catch(() => {
            // handle system errors using a global axios interceptor?
            dispatch({ type: types.TRIP_PLANNER_END_SEARCH_RESULTS });
        });
};

// load trip
export function loadTrip(tripId) {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/trips/${tripId}`, {
                headers: {
                    Authorization: SecurityHelper.getAuthHeader(),
                },
            })
            .then((json) => {
                if (json.data.err) {
                    dispatch({
                        type: types.TRIP_PLANNER_ERROR,
                        message: 'Could not load trip',
                    });
                    throw json.data.err;
                } else {
                    dispatch({ type: types.TRIP_PLANNER_SET_TRIP, trip: json.data.trip });
                    dispatch(fetchTripItemsDetails(json.data.trip));
                    dispatch(fetchDrivingDirections(json.data.trip));
                    dispatch(fetchDetailedRoute(json.data.trip));
                }
            })
            .catch(() => {
                // handle system errors using a global axios interceptor?
            });
    };
}

// Fetch saved user trips
export function fetchUserTrips() {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/trips/list`, {
                params: {},
                headers: {
                    Authorization: SecurityHelper.getAuthHeader(),
                },
            })
            .then((json) => {
                if (json.data.err) {
                    dispatch({
                        type: types.TRIP_PLANNER_LOAD_USER_TRIPS,
                        message: 'Error fetching suggestions',
                    });
                } else {
                    dispatch({
                        type: types.TRIP_PLANNER_LOAD_USER_TRIPS,
                        trips: json.data.trips,
                    });
                }
            })
            .catch(() => {
                // handle system errors using a global axios interceptor?
            });
    };
}

// load trip
export function deleteTrip(tripId) {
    return (dispatch) => {
        return axios
            .delete(`${process.env.API}/trips/${tripId}`, {
                headers: {
                    Authorization: SecurityHelper.getAuthHeader(),
                },
            })
            .then((json) => {
                if (json.data.err) {
                    dispatch({
                        type: types.TRIP_PLANNER_ERROR,
                        message: 'Could not delete trip',
                    });
                    throw json.data.err;
                } else {
                    dispatch(fetchUserTrips());
                }
            })
            .catch(() => {
                // handle system errors using a global axios interceptor?
            });
    };
}

// fetch inventory search results for list display
export const fetchTripPlannerListSearchResults =
    (destination, destinationIndex) => (dispatch, getState) => {
        const trip = getState().tripPlanner.trip;
        const params = {
            filters: [],
            uiFilters: getState().tripPlanner.uiFilters,
            start: destination.start,
            size: types.DESTINATION_RESULT_PAGE_SIZE,
            lat: destination.lat,
            lng: destination.lng,
            exact: true,
            sort: 'score',
            fg: getState().tripPlanner.fg,
        };
        if (getState().tripPlanner.trip.radius !== -1) {
            params.radius = milesToKms(getState().tripPlanner.trip.radius);
        }

        const tripStartEndDates = getTripStartEndDates(trip);
        const tripStartDate = tripStartEndDates[0];
        const tripEndDate = tripStartEndDates[1];

        if (tripStartDate) {
            params.start_date = formatTripPlannerDate(tripStartDate);
        }
        if (tripEndDate) {
            params.end_date = formatTripPlannerDate(tripEndDate);
        }

        // exclude indiviual tours from results
        params.filters = [
            {
                attribute: '-entity_type',
                values: [{ value: '(tour OR timedentry_tour)' }],
            },
        ];

        const args = buildSearchServiceRequest(params);
        return axios
            .post(`${process.env.API}/search`, args)
            .then((json) => {
                if (json.data.err) {
                    dispatch({
                        type: types.GET_TRIP_PLANNER_SEARCH_ERROR,
                        message: 'Error fetching search results',
                    });
                } else {
                    const results = json.data;
                    dispatch({
                        type: types.GET_TRIP_PLANNER_SEARCH_RESULTS,
                        listResults: results,
                        index: destinationIndex,
                    });
                }
            })
            .catch(() => {
                // handle system errors using a global axios interceptor?
            });
    };

export const getInventoryItemDetails = (item) => (dispatch) => {
    const params = {
        start: 0,
        size: MAX_GEO_RESULTS,
        filters: [
            {
                attribute: 'id',
                values: [
                    {
                        value: getSearchDocId(item),
                    },
                ],
            },
        ],
    };
    const args = buildSearchServiceRequest(params);
    return axios
        .post(`${process.env.API}/search`, args)
        .then((json) => {
            if (json.data.err) {
                dispatch({
                    type: types.GET_TRIP_INVENTORY_ITEM_DETAILS_ERROR,
                    message: 'Error fetching details search results',
                });
            } else {
                const results = json.data;
                dispatch({
                    type: types.GET_TRIP_INVENTORY_ITEM_DETAILS,
                    inventoryItemDetails: results,
                });
            }
        })
        .catch(() => {
            // handle system errors using a global axios interceptor?
        });
};

// Fetch saved user preferences
export function fetchUserPreferences() {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/trips/preferences`, {
                params: {},
                headers: {
                    Authorization: SecurityHelper.getAuthHeader(),
                },
            })
            .then((json) => {
                if (json.data.err) {
                    dispatch({
                        type: types.TRIP_PLANNER_LOAD_USER_PREFERENCES,
                        message: 'Error fetching user preferences',
                    });
                } else {
                    dispatch({
                        type: types.TRIP_PLANNER_LOAD_USER_PREFERENCES,
                        preferences: json.data.preferences,
                    });
                }
            })
            .catch(() => {
                // handle system errors using a global axios interceptor?
            });
    };
}

// save user preferences
export const saveUserPreferences = () => (dispatch, getState) => {
    const state = getState();
    const preferences = state.tripPlanner.preferences;

    const params = {
        preferences,
    };

    const url = `${process.env.API}/trips/preferences`;

    return axios
        .post(url, params, {
            headers: {
                Authorization: SecurityHelper.getAuthHeader(),
            },
        })
        .then((json) => {
            if (json.data.err) {
                throw new Error(`Error saving user preferences ${json.data.err}`);
            }
        })
        .catch(() => {
            // handle system errors using a global axios interceptor?
        });
};

export function setHideIntro(shouldHideIntro) {
    return {
        type: types.TRIP_PLANNER_SET_HIDE_INTRO,
        hide_intro: shouldHideIntro,
    };
}

export function setHideBookingIntro(shouldHideBookingIntro) {
    return {
        type: types.TRIP_PLANNER_SET_HIDE_BOOKING_INTRO,
        hide_booking_intro: shouldHideBookingIntro,
    };
}

// Fetch trip ideas
export function fetchTripIdeas() {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/trips/ideas`, {
                params: {},
                headers: {
                    Authorization: SecurityHelper.getAuthHeader(),
                },
            })
            .then((json) => {
                if (json.data.err) {
                    dispatch({
                        type: types.TRIP_PLANNER_LOAD_TRIP_IDEAS,
                        message: 'Error fetching suggestions',
                    });
                } else {
                    dispatch({
                        type: types.TRIP_PLANNER_LOAD_TRIP_IDEAS,
                        trips: json.data.trips,
                    });
                }
            })
            .catch(() => {
                // handle system errors using a global axios interceptor?
            });
    };
}

// empties trip planner state from any currently cached results
export const resetResults = () => (dispatch) => {
    dispatch({
        type: types.GET_DIRECTIONS,
        directions: null,
    });
    dispatch({
        type: types.GET_TRIP_PLANNER_RESET_AGGREGATIONS,
    });
    dispatch({
        type: types.GET_TRIP_PLANNER_RESET_RESULTS,
    });
};

export function addDestination(destination) {
    return (dispatch) => {
        dispatch({
            type: types.TRIP_PLANNER_ADD_DESTINATION,
            destination,
        });

        // autosave
        setTimeout(() => {
            dispatch(saveTrip());
        }, 100);
    };
}

export function setTrip(trip, save) {
    return (dispatch) => {
        dispatch({
            type: types.TRIP_PLANNER_SET_TRIP,
            trip,
        });

        if (save) {
            // autosave
            setTimeout(() => {
                dispatch(saveTrip());
            }, 100);
        }
        dispatch(fetchTripItemsDetails());
    };
}

export function updateDestination(destination, index) {
    return (dispatch) => {
        dispatch({
            type: types.TRIP_PLANNER_UPDATE_DESTINATION,
            destination,
            index,
        });

        // autosave
        setTimeout(() => {
            dispatch(saveTrip());
        }, 100);
    };
}

export function deleteDestination(index) {
    return (dispatch) => {
        dispatch({
            type: types.TRIP_PLANNER_DELETE_DESTINATION,
            index,
        });

        // autosave
        setTimeout(() => {
            dispatch(saveTrip());
        }, 100);
    };
}

export function moveDestination(fromIndex, toIndex) {
    return (dispatch) => {
        dispatch({
            type: types.TRIP_PLANNER_MOVE_DESTINATION,
            fromIndex,
            toIndex,
        });
        // autosave
        setTimeout(() => {
            dispatch(saveTrip());
        }, 100);
    };
}

export function addItem(destinationIndex, item) {
    return (dispatch) => {
        dispatch({
            type: types.TRIP_PLANNER_ADD_ITEM,
            destinationIndex,
            item,
        });
        // autosave
        setTimeout(() => {
            dispatch(saveTrip());
        }, 100);
    };
}

export function deleteItem(destinationIndex, item) {
    return (dispatch) => {
        dispatch({
            type: types.TRIP_PLANNER_DELETE_ITEM,
            destinationIndex,
            item,
        });
        // autosave
        setTimeout(() => {
            dispatch(saveTrip());
        }, 100);
    };
}

// update filters visibility status
export function setFiltersVisible(isFiltersVisible) {
    return {
        type: types.TRIP_PLANNER_SET_FILTERS_VISIBLE,
        filtersVisible: isFiltersVisible,
    };
}

export function resetSearchFilters() {
    return {
        type: types.TRIP_PLANNER_RESET_FILTERS,
    };
}

// update search criteria by adding a filter
export function addSearchFilter(uiFilterName, uiFilterOption) {
    return {
        type: types.TRIP_PLANNER_ADD_SEARCH_FILTER,
        uiFilterName,
        uiFilterOption,
    };
}

// update search criteria by removing a filter
export function removeSearchFilter(uiFilterName, uiFilterOption) {
    return {
        type: types.TRIP_PLANNER_REMOVE_SEARCH_FILTER,
        uiFilterName,
        uiFilterOption,
    };
}

// update search criteria by toggling filter value
export function toggleSearchFilter(uiFilterName, uiFilterOption) {
    return (dispatch, getState) => {
        const filterOptions = getState().tripPlanner.uiFilters[uiFilterName];
        const removeFilter = filterOptions[uiFilterOption.key];

        if (removeFilter) {
            return dispatch(removeSearchFilter(uiFilterName, uiFilterOption));
        }

        return dispatch(addSearchFilter(uiFilterName, uiFilterOption));
    };
}

// update criteria by toggling filter value
export function toggleFilter(uiFilterName, uiFilterOption) {
    return (dispatch, getState) => {
        const filterOptions = getState().tripPlanner.uiFilters[uiFilterName];

        const filterToRemove = filterOptions[uiFilterOption.key];

        if (filterToRemove) {
            return dispatch(removeSearchFilter(uiFilterName, uiFilterOption));
        }

        return dispatch(addSearchFilter(uiFilterName, uiFilterOption));
    };
}

// update search criteria by setting a filter value
export function setSearchFilter(uiFilterName, uiFilterOption) {
    return {
        type: types.TRIP_PLANNER_SET_SEARCH_FILTER,
        uiFilterName,
        uiFilterOption,
    };
}

// fetch aggregations for attributes of a UI Filter
export function fetchFilterAggregations(
    uiFilterName,
    aggregateAttributes,
    additionalFilters
) {
    return (dispatch, getState) => {
        const state = cloneState(getState());

        const params = {
            filters: [],
            uiFilters: getState().tripPlanner.uiFilters,
            routePolygonStr: state.tripPlannerSearchResults.routePolygonStr,
            start: 0,
            size: 0,
        };

        if (getState().tripPlanner.trip.radius !== -1) {
            params.radius = getState().tripPlanner.trip.radius;
        }

        if (additionalFilters) {
            additionalFilters.forEach((filter) => {
                params.filters[params.filters.length] = filter;
            });
        }

        params.aggregations = aggregateAttributes;
        params.size = 0;

        const searchUrl = `${process.env.API}/search`;
        // include all but filters for current uiFilterName
        const uiFilters = {};
        for (const propName in params.uiFilters) {
            if (propName !== uiFilterName) {
                uiFilters[propName] = params.uiFilters[propName];
            }
        }
        params.uiFilters = uiFilters;

        const args = buildSearchServiceRequest(params);
        return axios
            .post(searchUrl, args)
            .then((json) => {
                if (json.data.err) {
                    dispatch({
                        type: types.GET_TRIP_PLANNER_SEARCH_ERROR,
                        message: 'Error fetching search results',
                    });
                } else {
                    dispatch({
                        type: types.GET_TRIP_PLANNER_AGGREGATIONS,
                        uiFilterName,
                        payload: json.data,
                    });
                }
            })
            .catch(() => {
                // handle system errors using a global axios interceptor?
            });
    };
}

export function fetchFilterGroupAggregations(
    groupName,
    invType,
    isCampsites,
    additionalFilters
) {
    return (dispatch, getState) => {
        const state = cloneState(getState());

        const params = {
            filters: [],
            uiFilters: getState().tripPlanner.uiFilters,
            routePolygonStr: state.tripPlannerSearchResults.routePolygonStr,
            start: 0,
            size: 0,
        };

        if (getState().tripPlanner.trip.radius !== -1) {
            params.radius = getState().tripPlanner.trip.radius;
        }

        if (additionalFilters) {
            additionalFilters.forEach((filter) => {
                params.filters[params.filters.length] = filter;
            });
        }

        params.size = 0;
        params.group_agg = groupName;
        params.fg = params.tripPlanner?.fg;

        const searchUrl = `${process.env.API}/search`;

        const legacyKeys = [
            'campsiteTypes',
            'campsiteAmenities',
            'campsiteElectricityHookup',
            'campsiteEquipment',
        ];

        const uiFilters = {};
        if (params?.uiFilters) {
            for (const propName in params?.uiFilters) {
                if (legacyKeys.indexOf(propName) === -1) {
                    uiFilters[propName] = params.uiFilters[propName];
                }
            }
        }
        params.uiFilters = uiFilters;
        const args = buildSearchServiceRequest(params);

        const initialAggregations = [{ name: 'camping', count: 0 }];

        return axios
            .post(searchUrl, args)
            .then((json) => {
                if (json.err) {
                    dispatch({
                        type: types.GET_TRIP_PLANNER_SEARCH_ERROR,
                        message: 'Error fetching search results',
                    });
                } else {
                    let payload = {};
                    const result = get(
                        json?.data,
                        'aggregations["group-aggregations"]',
                        initialAggregations
                    );

                    result.forEach((item) => {
                        payload = { ...payload, [item.name]: item.count };
                    });

                    dispatch({
                        type: types.GET_TRIP_PLANNER_GROUP_AGGREGATIONS,
                        groupName: invType,
                        aggregations: payload,
                    });
                }
            })
            .catch(() => {
                dispatch({
                    type: types.GET_TRIP_PLANNER_SEARCH_ERROR,
                    message: 'Error fetching search results',
                });
            });
    };
}

// fetch aggregations for ui search filters
export function fetchSearchFiltersAggregations() {
    return async (dispatch) => {
        dispatch(
            fetchFilterGroupAggregations(
                ['booking-types'],
                'inventoryTypes',
                false,
                null,
                true
            )
        );
        dispatch(
            fetchFilterGroupAggregations(
                [
                    'camping-type',
                    'electricity',
                    'key-characteristics',
                    'access',
                    'special-site-type',
                    'amenities',
                ],
                'camping'
            )
        );
        dispatch(fetchFilterAggregations('agencies', ['org_id']));
        dispatch(fetchFilterAggregations('entityTypes', ['entity_type']));
        dispatch(fetchFilterAggregations('activities', ['asset_activities']));
        dispatch(
            fetchFilterAggregations(
                'tourTypes',
                ['tour_type'],
                [
                    {
                        attribute: 'entity_type',
                        values: [{ label: '', value: 'tour' }],
                    },
                ]
            )
        );

        dispatch(
            fetchFilterAggregations(
                'tourDifficulty',
                ['att_physical_effort'],
                [
                    {
                        attribute: 'entity_type',
                        values: [{ label: '', value: 'tour' }],
                    },
                ]
            )
        );

        dispatch(
            fetchFilterAggregations(
                'tourAccessible',
                ['tour_accessible'],
                [
                    {
                        attribute: 'entity_type',
                        values: [{ label: '', value: 'tour' }],
                    },
                ]
            )
        );
        /*
        dispatch(
          fetchFilterAggregations("permitTypes", ["permit_types"])
        );
        */
    };
}

// update search location
export function setSearchLocation(searchLocation) {
    return {
        type: types.TRIP_PLANNER_SET_SEARCH_LOCATION,
        searchLocation,
    };
}

// update search criteria state with a new value for a name parameter
export function updateTripPlannerCriteria(name, value) {
    return (dispatch) => {
        return dispatch({
            type: types.UPDATE_TRIP_PLANNER_CRITERIA,
            name,
            value,
        });
    };
}
